import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CustomerType, FulfillmentType, Region } from '@kalachand/shared';
import { effectiveDiscountPct, DEFAULT_SALES_DISCOUNT_CAP_PCT, FAST_TRACK_DELIVERY_FEE } from '@kalachand/shared';
import { apiGet, apiPatch, apiPost, getApiError } from '../../lib/api';
import { useSubmitGuard } from '../../lib/useSubmitGuard';
import { useAuth } from '../../lib/auth';
import { cart, useCart } from '../../lib/cart';
import { Button } from '../../components/Button';
import { IconButton } from '../../components/IconButton';
import { PageScaffold } from '../../components/PageScaffold';
import { Input } from '../../components/Input';
import { Card } from '../../components/Card';
import { Money } from '../../components/Money';
import { emptyPayment, type PaymentDraft } from '../../components/PaymentFields';
import { PaymentCapture, paymentsValid, paymentsTotal } from '../../components/PaymentCapture';
import { EmptyState } from '../../components/EmptyState';
import { Stepper } from '../../components/Stepper';
import { DiscountInput } from '../../components/DiscountInput';
import { cn } from '../../lib/cn';
import { businessTodayIso, addDaysIso, formatBusinessDate } from '../../lib/dates';
import { orderDiscountAmount, allocateDiscount, formatPct, type DiscountMode } from '../../lib/discount';
import { DeliveryDatePicker } from '../../components/DeliveryDatePicker';
import { AddressAutocomplete } from '../../components/AddressAutocomplete';
import { CustomerCreateForm } from './CustomerCreateForm';
import { CustomSizeModal } from './CustomSizeModal';

export interface Customer {
  id: string;
  customerCode: string;
  type: CustomerType;
  name: string;
  phone: string;
  email: string | null;
  companyName: string | null;
  vatNumber: string | null;
  brn: string | null;
  contactPerson: string | null;
  vatExcluded: boolean;
  address: string | null;
  addressPrecise: string | null;
  city: string | null;
  country: string | null;
  region: Region | null;
  nationalId?: string | null;
  dateOfBirth?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  notes: string | null;
  preferredShopId: string | null;
  preferredShop?: { id: string; code: string; name: string } | null;
}

interface StockSnapshot {
  onHand: number;
  reserved: number;
  display: number; // showroom floor pieces — present but not sellable
  location: { id: string; kind: 'warehouse' | 'shop'; shopId: string | null };
}

// One sellable size the sales girl can pick for a model gift.
interface GiftSizeOption {
  productId: string;
  sku: string;
  size: string | null;
  available: number | null; // null = made-to-order size (always added)
}
// A free gift the product's model bundles (opt-in, added at no charge). Either a
// FIXED variant (productId + available) or a MODEL gift (giftModelId + sizeOptions
// — the sales girl picks the size at order time).
interface GiftDef {
  productId: string | null;
  giftModelId: string | null;
  sku: string | null;
  name: string;
  qty: number;
  available: number | null; // null = made-to-order gift (always added)
  sizeOptions: GiftSizeOption[] | null;
}
export interface Product {
  id: string;
  sku: string;
  name: string;
  modelId: string | null;
  basePrice: string;
  salePrice: string | null;
  vatRate: string;
  category: {
    id: string;
    name: string;
    code: string;
    fulfillmentStrategy: string;
    deliveryRequired: boolean;
    allowsCustomSize: boolean;
  };
  stockLevels?: StockSnapshot[];
  gifts?: GiftDef[];
}

interface Shop {
  id: string;
  code: string;
  name: string;
  type: 'HQ' | 'SHOP' | 'ONLINE';
  isHeadOffice: boolean;
}

interface LineDraft {
  // Line identity for update/remove + React key. For a custom-size line this is a
  // synthetic id (several custom lines can share the matched variant), and the
  // real product to send to the API lives in `matchedVariantId`.
  productId: string;
  product: { sku: string; name: string; vatRate: number };
  qty: number;
  unitPrice: number;
  // ── Custom mattress size (bespoke W×L on a local made-to-order mattress) ──
  // When isCustom, `unitPrice` is the server-derived ladder price (locked in the
  // UI; the server recomputes it on confirm) and `matchedVariantId` is the
  // standard variant the price was matched to.
  isCustom?: boolean;
  matchedVariantId?: string;
  customWidthCm?: number;
  customLengthCm?: number;
  matchedSize?: string;
  colorOptions?: string[];
  sideColorOptions?: string[];
  fabricOptions?: string[];
  densityOptions?: string[];
  selectedColor?: string | null;
  selectedSideColor?: string | null;
  selectedFabric?: string | null;
  selectedDensity?: string | null;
  deliveryRequired: boolean;
  strategy: string;
  // Stock snapshot at add time. Empty for non-stocked categories
  // (made-to-order, supplier). Drives "no stock → no add" and
  // "shop out → force delivery from HQ".
  stockLevels: StockSnapshot[];
  // Explicit backorder: an out-of-stock imported line reserved against incoming
  // HQ stock. Forces delivery; allocated FIFO when the container is received.
  backorder: boolean;
  // Hand this stocked line over at the counter when the order confirms (delivery
  // orders only): the customer takes the SELLING SHOP's unit now; the rest of
  // the order still goes out on delivery. Defaults ON when the shop has stock.
  handoverNow: boolean;
  // The sales girl toggled hand-over-now herself — stop auto-deriving it.
  handoverNowTouched?: boolean;
  // Free gift(s) the model bundles. Opt-in: the gift is only added when
  // `includeGift` is ticked (no longer auto-allocated). The backend still handles
  // OOS skips for an opted-in gift.
  gifts?: GiftDef[];
  includeGift?: boolean;
  // Chosen size per MODEL gift on this line, keyed by giftModelId → chosen
  // variant productId. Only model gifts need an entry; fixed gifts are ignored.
  giftSelections?: Record<string, string>;
}

function avail(level: StockSnapshot): number {
  return Math.max(0, level.onHand - level.reserved - level.display);
}

// Pre-pick a size for each MODEL gift so the line has a valid default: the first
// in-stock size, else the first size. Fixed-variant gifts contribute nothing.
function defaultGiftSelections(gifts?: GiftDef[]): Record<string, string> {
  const sel: Record<string, string> = {};
  for (const g of gifts ?? []) {
    if (!g.giftModelId || !g.sizeOptions?.length) continue;
    const inStock = g.sizeOptions.find((o) => o.available == null || o.available >= g.qty);
    sel[g.giftModelId] = (inStock ?? g.sizeOptions[0]!).productId;
  }
  return sel;
}

// The API-shaped size picks for a line's model gifts: [{ giftModelId, productId }].
function giftSelectionsFor(l: LineDraft): { giftModelId: string; productId: string }[] {
  return (l.gifts ?? [])
    .filter((g) => g.giftModelId)
    .map((g) => ({ giftModelId: g.giftModelId!, productId: l.giftSelections?.[g.giftModelId!] ?? '' }))
    .filter((s) => s.productId);
}
function totalAvailable(line: { stockLevels: StockSnapshot[] }): number {
  return line.stockLevels.reduce((s, l) => s + avail(l), 0);
}
function availableAtShop(line: { stockLevels: StockSnapshot[] }, shopId: string | null): number {
  if (!shopId) return 0;
  return line.stockLevels
    .filter((l) => l.location.shopId === shopId)
    .reduce((s, l) => s + avail(l), 0);
}
function availableAtWarehouse(line: { stockLevels: StockSnapshot[] }): number {
  return line.stockLevels
    .filter((l) => l.location.kind === 'warehouse')
    .reduce((s, l) => s + avail(l), 0);
}
/** A stocked line we can't fully cover from on-hand stock (HQ + shops). */
function lineOutOfStock(line: {
  strategy: string;
  qty: number;
  stockLevels: StockSnapshot[];
}): boolean {
  return line.strategy === 'STOCK_AT_LOCATION' && totalAvailable(line) < line.qty;
}

// Frontend mirror of the backend `willFulfilFromStock`: would this line ship
// straight from stock on hand (→ next-day lead) rather than being built/ordered?
// Conservative — the backend re-decides authoritatively at order create. A custom
// size, or a line whose model offers a FREE finish option (colour/fabric/side/
// density chosen at order time, not stocked per-finish), is never from-stock.
// Delivery draws from the warehouse, pickup from the shop — same as confirm.
function lineShipsFromStock(
  line: {
    qty: number;
    customWidthCm?: number;
    colorOptions?: string[];
    sideColorOptions?: string[];
    fabricOptions?: string[];
    densityOptions?: string[];
    stockLevels: StockSnapshot[];
  },
  fulfillmentType: string,
  pickupShopId: string | null,
): boolean {
  if (line.customWidthCm != null) return false;
  if (
    line.colorOptions?.length ||
    line.sideColorOptions?.length ||
    line.fabricOptions?.length ||
    line.densityOptions?.length
  )
    return false;
  const available =
    fulfillmentType === 'pickup'
      ? availableAtShop(line, pickupShopId)
      : line.stockLevels.filter((l) => l.location.kind === 'warehouse').reduce((s, l) => s + avail(l), 0);
  return available >= line.qty;
}

// Earliest delivery lead (days from today) by fulfilment strategy — mirrors the
// backend rule. Imported/accessories next day, local 3 days, bed base 10.
const LEAD_BY_STRATEGY: Record<string, number> = {
  STOCK_AT_LOCATION: 1,
  MADE_TO_ORDER_FACTORY: 3,
  MADE_TO_ORDER_SUPPLIER: 10,
};

interface ExchangeContext {
  customerId: string;
  creditNoteId: string;
  creditNumber: string;
  creditAmount: string;
  fromNumber: string;
}

export function NewOrderPage(): JSX.Element {
  const { user, perms } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const location = useLocation();
  const exchange = (location.state as { exchange?: ExchangeContext } | null)?.exchange;
  const cartView = useCart();

  // Seed lines from cart on mount.
  const [lines, setLines] = useState<LineDraft[]>(() =>
    cartView.items.map((c) => ({
      productId: c.productId,
      product: { sku: c.sku, name: c.name, vatRate: c.vatRate },
      qty: c.qty,
      unitPrice: c.unitPrice,
      colorOptions: c.colorOptions,
      sideColorOptions: c.sideColorOptions,
      fabricOptions: c.fabricOptions,
      densityOptions: c.densityOptions,
      selectedColor: c.selectedColor ?? null,
      selectedSideColor: c.selectedSideColor ?? null,
      selectedFabric: c.selectedFabric ?? null,
      selectedDensity: c.selectedDensity ?? null,
      deliveryRequired: c.deliveryRequired ?? false,
      strategy: c.fulfillmentStrategy ?? 'STOCK_AT_LOCATION',
      // Cart doesn't carry stock; we refresh per-line below.
      stockLevels: [],
      backorder: false,
      handoverNow: false,
      // Bespoke-size lines carried over from the catalogue cart.
      isCustom: c.isCustom,
      matchedVariantId: c.matchedVariantId,
      customWidthCm: c.customWidthCm,
      customLengthCm: c.customLengthCm,
      matchedSize: c.matchedSize,
      gifts: c.gifts ?? [],
      includeGift: false,
      giftSelections: defaultGiftSelections(c.gifts),
    })),
  );

  // Refresh stock snapshots for cart-seeded lines (they came from the catalogue
  // page without stock data). Fetches in parallel; no-op for lines that
  // already have snapshots. Out-of-stock STOCK_AT_LOCATION lines default to
  // reserved (backorder), same as addLine(). Made-to-order lines load stock too
  // so the calendar can offer next-day when we hold the exact SKU (lineOutOfStock
  // never flags them as backorder). Custom sizes never draw from stock.
  useEffect(() => {
    const stale = lines.filter(
      (l) => l.customWidthCm == null && l.stockLevels.length === 0,
    );
    if (stale.length === 0) return;
    Promise.all(stale.map((l) => apiGet<Product>(`/products/${l.productId}`).catch(() => null)))
      .then((products) => {
        setLines((current) =>
          current.map((line) => {
            const fresh = products.find((p) => p?.id === line.productId);
            if (!fresh || line.stockLevels.length > 0) return line;
            const stockLevels = fresh.stockLevels ?? [];
            return { ...line, stockLevels, backorder: line.backorder || lineOutOfStock({ ...line, stockLevels }) };
          }),
        );
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [step, setStep] = useState(1);
  const [payments, setPayments] = useState<PaymentDraft[]>(() => [emptyPayment('')]);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [fulfillmentType, setFulfillmentType] = useState<FulfillmentType>('pickup');
  // Left empty on purpose — the default is resolved once shops load (HQ-level
  // users default to the head-office counter, sales girls to their home shop).
  const [sellingShopId, setSellingShopId] = useState<string>('');
  const [pickupShopId, setPickupShopId] = useState<string>('');
  // Cross-shop creators (HQ-level) attribute a branch sale to that shop's girl.
  const [salespersonId, setSalespersonId] = useState<string>('');

  const shopsList = useQuery({
    queryKey: ['shops'],
    queryFn: () => apiGet<Shop[]>('/shops'),
  });
  const shops = shopsList.data ?? [];
  // "HQ-level" = a cross-shop seller (owner/admin/operations), keyed off the
  // permission — NOT the absence of a home shop. Sales girls are shop-scoped.
  const isHQUser = perms.has('orders.view_all_shops');
  // Resolve the default selling shop once shops load. HQ-level users default to
  // the head-office counter (the shop flagged isHeadOffice); shop-scoped sellers
  // default to whichever shop they're currently switched to in the nav
  // ("Currently at") — switch there, not here, if she's assigned to more than
  // one shop (e.g. a branch plus ONLINE). The legacy HQ node is never a
  // selling default.
  useEffect(() => {
    if (sellingShopId || shops.length === 0) return;
    const fallback = isHQUser
      ? shops.find((s) => s.isHeadOffice) ?? shops.find((s) => s.type === 'SHOP') ?? shops[0]
      : shops.find((s) => s.id === user?.activeShopId) ?? shops.find((s) => s.id === user?.homeShopId) ?? shops[0];
    if (fallback) {
      setSellingShopId(fallback.id);
      if (!pickupShopId) setPickupShopId(fallback.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shops.length]);
  const sellingShop = shops.find((s) => s.id === sellingShopId);
  const isHeadOfficeShop = !!sellingShop?.isHeadOffice;
  // A head-office user selling for a physical branch picks which sales girl gets
  // the sale (and her commission). At the head-office counter the picker is
  // OPTIONAL — the sale may be booked under the creator ("Myself") OR a girl, and
  // either way it earns a pool share. HQ / Online are booked under the creator.
  const showSalesperson = isHQUser && !!sellingShop && sellingShop.type === 'SHOP';
  const salespersonRequired = showSalesperson && !isHeadOfficeShop;
  const salespeopleQuery = useQuery({
    queryKey: ['shops', sellingShopId, 'salespeople'],
    queryFn: () => apiGet<{ id: string; name: string }[]>(`/shops/${sellingShopId}/salespeople`),
    enabled: showSalesperson,
  });
  const salespeople = salespeopleQuery.data ?? [];
  // Auto-pick when a BRANCH has exactly one sales girl. At the head-office
  // counter we leave it on "Myself" (empty) so the creator's own sale is the default.
  useEffect(() => {
    if (salespersonRequired && !salespersonId && salespeople.length === 1) {
      setSalespersonId(salespeople[0]!.id);
    }
  }, [salespersonRequired, salespersonId, salespeople]);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [orderRegion, setOrderRegion] = useState<Region | null>(null);
  const [disposalQty, setDisposalQty] = useState(0);
  // Fast Track (priority) delivery — opt-in flat fee, delivery orders only.
  const [fastTrack, setFastTrack] = useState(false);
  const [notes, setNotes] = useState('');
  // Order-level discount — one figure for the whole order (replaces per-line
  // discounts). Allocated back across the lines proportional to each line's
  // gross when building totals + the payload, so the backend contract (per-line
  // discount + Order.discountTotal) is unchanged.
  const [orderDiscountMode, setOrderDiscountMode] = useState<DiscountMode>('pct');
  const [orderDiscountValue, setOrderDiscountValue] = useState(0);
  // The product whose model we're building a custom size for (opens the modal).
  const [customFor, setCustomFor] = useState<Product | null>(null);

  // When a customer is picked, default the order region to theirs.
  useEffect(() => {
    if (customer?.region) setOrderRegion(customer.region);
    else if (customer && !customer.region) setOrderRegion(null);
  }, [customer]);

  // Optional pre-select via ?productId=
  const preProductId = params.get('productId');
  const preProductQuery = useQuery({
    queryKey: ['product', preProductId],
    queryFn: () => apiGet<Product>(`/products/${preProductId}`),
    enabled: !!preProductId,
  });
  useEffect(() => {
    if (preProductQuery.data && !lines.some((l) => l.productId === preProductQuery.data!.id)) {
      addLine(preProductQuery.data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preProductQuery.data]);

  // Pre-fill delivery address from customer once selected
  useEffect(() => {
    if (customer && fulfillmentType === 'delivery' && !deliveryAddress) {
      const parts = [customer.address, customer.city, customer.country].filter(Boolean);
      setDeliveryAddress(parts.join(', '));
    }
  }, [customer, fulfillmentType, deliveryAddress]);

  // Exchange flow: pre-select the customer from the original (returned) order.
  useEffect(() => {
    if (exchange?.customerId && !customer) {
      apiGet<Customer>(`/customers/${exchange.customerId}`)
        .then(setCustomer)
        .catch(() => { });
    }
  }, [exchange, customer]);

  // Pickup is accessories-only AND requires enough stock at the pickup shop.
  // Anything that flunks either rule kicks the order onto delivery from HQ.
  const effectivePickupShopId = pickupShopId || sellingShopId || null;
  // Pickup is blocked ONLY by items that must be delivered (mattresses, beds). An
  // accessory that's short at the pickup shop is no longer a blocker — it becomes a
  // "collect from HQ" line (backorder under pickup): HQ transfers it to the shop
  // and the customer collects. The backorder flag is derived just below.
  const linePickupBlocker = (l: LineDraft): 'requires_delivery' | 'shop_out' | null => {
    if (l.deliveryRequired) return 'requires_delivery';
    return null;
  };
  const blockedLines = lines.map((l) => ({ line: l, reason: linePickupBlocker(l) }));
  const stockShortLines = blockedLines.filter((b) => b.reason === 'shop_out');
  const pickupBlocked = blockedLines.some((b) => b.reason !== null);

  useEffect(() => {
    if (lines.length > 0 && pickupBlocked && fulfillmentType === 'pickup') {
      setFulfillmentType('delivery');
    }
  }, [pickupBlocked, lines.length, fulfillmentType]);

  // Derive `handoverNow` + `backorder` for stocked ACCESSORIES from fulfilment
  // + availability:
  //   • pickup   → handoverNow is meaningless (the whole order is handed over at
  //                the shop); backorder = "collect from HQ" when short at the
  //                pickup shop (HQ transfers it in; the customer collects there)
  //   • delivery → handoverNow defaults ON when the SELLING SHOP has the item on
  //                the shelf (customer takes it at the counter now — that shop's
  //                stock is validated + decremented, mirroring the backend);
  //                the sales girl can untick it to send it with the delivery.
  //                A line NOT handed over now ships from the WAREHOUSE, so
  //                backorder = short at the warehouse (shop stock can't cover a
  //                delivery — matches the backend's warehouse-only check).
  // Imported lines (deliveryRequired) keep their add-time + manual-toggle backorder
  // behaviour and are left untouched here.
  useEffect(() => {
    setLines((ls) => {
      let changed = false;
      const next = ls.map((l) => {
        if (l.strategy !== 'STOCK_AT_LOCATION' || l.deliveryRequired || l.isCustom) return l;
        const shopCanCover = availableAtShop(l, sellingShopId || null) >= l.qty;
        const handoverNow =
          fulfillmentType === 'delivery' &&
          shopCanCover &&
          (l.handoverNowTouched ? l.handoverNow : true);
        const backorder =
          fulfillmentType === 'pickup'
            ? availableAtShop(l, effectivePickupShopId) < l.qty
            : !handoverNow && availableAtWarehouse(l) < l.qty;
        if (handoverNow === l.handoverNow && backorder === l.backorder) return l;
        changed = true;
        return { ...l, handoverNow, backorder };
      });
      return changed ? next : ls;
    });
  }, [fulfillmentType, effectivePickupShopId, sellingShopId, lines]);

  // Payment is mandatory — default the amount to the full balance on the Confirm
  // step (the sales girl reduces it for a deposit).
  useEffect(() => {
    if (step === 4) {
      setPayments((ps) =>
        ps.length === 1 && ps[0]!.amount === ''
          ? [{ ...ps[0]!, amount: String(totals.total) }]
          : ps,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const addLine = (p: Product): void => {
    setLines((existing) => {
      const found = existing.find((l) => l.productId === p.id);
      if (found) return existing.map((l) => (l.productId === p.id ? { ...l, qty: l.qty + 1 } : l));
      const stockLevels = p.stockLevels ?? [];
      // An out-of-stock imported line is added as a backorder by default (the
      // sales girl can untick it on the line). It reserves against incoming HQ stock.
      const backorder =
        p.category.fulfillmentStrategy === 'STOCK_AT_LOCATION' &&
        totalAvailable({ stockLevels }) < 1;
      return [
        ...existing,
        {
          productId: p.id,
          product: { sku: p.sku, name: p.name, vatRate: Number(p.vatRate) },
          qty: 1,
          unitPrice: Number(p.salePrice ?? p.basePrice),
          deliveryRequired: p.category.deliveryRequired,
          strategy: p.category.fulfillmentStrategy,
          stockLevels,
          backorder,
          handoverNow: false,
          gifts: p.gifts ?? [],
          includeGift: false,
          giftSelections: defaultGiftSelections(p.gifts),
        },
      ];
    });
  };
  const updateLine = (id: string, patch: Partial<LineDraft>): void => {
    setLines((existing) => existing.map((l) => (l.productId === id ? { ...l, ...patch } : l)));
    // Mirror quantity edits to the shared cart so the sticky cart bar's unit
    // count stays accurate once the user leaves this page (the bar is hidden
    // here but reads from the cart, which seeded these lines).
    if (typeof patch.qty === 'number') cart.update(id, { qty: patch.qty });
  };
  const removeLine = (id: string): void => {
    setLines((existing) => existing.filter((l) => l.productId !== id));
    // Keep the shared cart in sync — without this the removed product lingers in
    // the cart store and the bottom cart-bar count never drops.
    cart.remove(id);
  };

  // A bespoke-size line never merges with another — each gets a fresh synthetic id.
  const addCustomLine = (line: LineDraft): void => setLines((existing) => [...existing, line]);

  const vatExempt = customer?.vatExcluded ?? false;

  // Gross of the lines that can carry a discount (custom-size lines never do —
  // their price is server-derived from the size ladder). The order discount is a
  // % of / capped at this figure.
  const discountableGross = useMemo(
    () => lines.reduce((s, l) => (l.isCustom ? s : s + l.qty * l.unitPrice), 0),
    [lines],
  );
  // The single order-level discount, in money.
  const orderDiscountTotal = useMemo(
    () => orderDiscountAmount(discountableGross, orderDiscountMode, orderDiscountValue),
    [discountableGross, orderDiscountMode, orderDiscountValue],
  );
  // Allocate it back across the lines, proportional to each line's gross, so the
  // per-line VAT split + the backend payload still work line-by-line. Keyed by
  // productId. Custom lines get 0 (their gross is excluded above).
  const lineDiscounts = useMemo(() => {
    const grosses = lines.map((l) => (l.isCustom ? 0 : l.qty * l.unitPrice));
    const parts = allocateDiscount(grosses, orderDiscountTotal);
    const m = new Map<string, number>();
    lines.forEach((l, i) => m.set(l.productId, parts[i] ?? 0));
    return m;
  }, [lines, orderDiscountTotal]);

  const totals = useMemo(() => {
    let subtotal = 0;
    let vat = 0;
    let discountTotal = 0;
    let grossTotal = 0;
    for (const l of lines) {
      const gross = l.qty * l.unitPrice;
      const lineDisc = lineDiscounts.get(l.productId) ?? 0;
      // Catalogue prices are VAT-INCLUSIVE — extract VAT from the line gross
      // (× rate/(1+rate)) rather than adding on top, matching the backend.
      const grossIncl = gross - lineDisc;
      const r = l.product.vatRate;
      const lineVat = vatExempt ? 0 : Math.round(((grossIncl * r) / (1 + r)) * 100) / 100;
      const lineNet = grossIncl - lineVat;
      subtotal += lineNet;
      vat += lineVat;
      discountTotal += lineDisc;
      grossTotal += gross;
    }
    // Order-level effective discount (money discount ÷ pre-discount gross) — the
    // figure the 3% cap is checked against, matching the backend.
    const discountPct = effectiveDiscountPct(grossTotal, discountTotal);
    // Fast Track fee (delivery + opted-in only). VAT-inclusive gross added on top
    // of the goods total — shown as its own line; matches the backend total.
    const fastTrackFee = fastTrack && fulfillmentType === 'delivery' ? FAST_TRACK_DELIVERY_FEE : 0;
    return {
      subtotal,
      vat,
      discountTotal,
      fastTrackFee,
      total: subtotal + vat + fastTrackFee,
      grossTotal,
      discountPct,
    };
  }, [lines, vatExempt, lineDiscounts, fastTrack, fulfillmentType]);

  // Sales-discount cap (fraction of gross) for this company. Anyone without
  // `discounts.apply_above_ceiling` (i.e. a salesperson) must get owner approval
  // to exceed it; owner / admin are uncapped.
  const companyQuery = useQuery({
    queryKey: ['company'],
    queryFn: () => apiGet<{ salesDiscountCapPct: string; depositThresholdPct: string }>('/company'),
  });
  const capPct = companyQuery.data
    ? Number(companyQuery.data.salesDiscountCapPct)
    : DEFAULT_SALES_DISCOUNT_CAP_PCT;
  // Minimum deposit (fraction of total) — surfaced as a one-tap "Deposit N%"
  // quick-fill AND enforced as the floor the confirm needs to clear. The backend
  // gate (assertOrderPaymentVerified) stays authoritative; this just blocks the
  // order at creation rather than letting it stall later at production/delivery.
  const depositPct = companyQuery.data ? Number(companyQuery.data.depositThresholdPct) : 0.3;
  // Smallest payment that clears the floor: total × pct, to the cent.
  const minDeposit = Math.round(totals.total * depositPct * 100) / 100;
  // Entered payment must be a valid tender AND reach the deposit floor (1-cent
  // tolerance so the rounded-up quick-fill never lands just under).
  const depositMet = paymentsTotal(payments) + 0.01 >= minDeposit;
  const canConfirm = paymentsValid(payments, totals.total) && depositMet;
  const canSelfApprove = perms.has('discounts.apply_above_ceiling');
  const overCap = totals.discountPct > capPct + 1e-6;
  const needsApproval = overCap && !canSelfApprove;
  const [approvalReason, setApprovalReason] = useState('');

  // Compose the POST /orders body from the wizard state. Shared by the confirm
  // path and the over-cap "request approval" path.
  const buildOrderBody = (
    allowImportMix = false,
    allowBackorderReservation = false,
  ): Record<string, unknown> => {
    if (!sellingShopId) throw new Error('Pick which shop this order is sold from');
    if (salespersonRequired && !salespersonId) {
      throw new Error('Pick which salesperson this sale is attributed to');
    }
    if (!customer) throw new Error('Pick a customer first');
    if (lines.length === 0) throw new Error('Add at least one line item');
    if (fulfillmentType === 'delivery' && !deliveryAddress.trim()) {
      throw new Error('Delivery address required for delivery orders');
    }
    return {
      shopId: sellingShopId,
      ...(salespersonId ? { salespersonId } : {}),
      customerId: customer.id,
      fulfillmentType,
      pickupShopId: fulfillmentType === 'pickup' ? pickupShopId || sellingShopId : null,
      deliveryAddress: fulfillmentType === 'delivery' ? deliveryAddress : null,
      // Precise address lives on the customer now — the server snapshots it.
      deliveryDate:
        fulfillmentType === 'delivery' && !hasBackorder && deliveryDate ? deliveryDate : null,
      region: fulfillmentType === 'delivery' ? orderRegion : null,
      disposalQty: fulfillmentType === 'delivery' ? disposalQty : 0,
      fastTrack: fulfillmentType === 'delivery' ? fastTrack : false,
      notes: notes || null,
      allowImportMix,
      allowBackorderReservation,
      lines: lines.map((l) => ({
        productId: l.isCustom ? l.matchedVariantId! : l.productId,
        qty: l.qty,
        // Custom lines: server recomputes the price from the size ladder; we still
        // send it (it's ignored) so the rest of the payload stays uniform.
        unitPrice: l.unitPrice,
        // Custom sizes are never discounted (enforced server-side too); other
        // lines carry their share of the order-level discount.
        discount: l.isCustom ? 0 : (lineDiscounts.get(l.productId) ?? 0),
        selectedColor: l.selectedColor ?? null,
        selectedSideColor: l.selectedSideColor ?? null,
        selectedFabric: l.selectedFabric ?? null,
        selectedDensity: l.selectedDensity ?? null,
        ...(l.isCustom ? { customWidthCm: l.customWidthCm, customLengthCm: l.customLengthCm } : {}),
        backorder: l.backorder,
        handoverNow: l.handoverNow,
        // Only send the gift opt-in for lines that actually bundle one.
        includeGift: (l.gifts?.length ?? 0) > 0 ? !!l.includeGift : undefined,
        // Chosen size(s) for this line's model gift(s) — only when opted in.
        ...(l.includeGift ? { giftSelections: giftSelectionsFor(l) } : {}),
      })),
    };
  };

  // One guard shared by every order-creating button (Save draft / Confirm /
  // Request approval) — the first tap locks out the rest until it settles, so a
  // same-tick double-tap can't POST /orders twice and place a duplicate order.
  const guard = useSubmitGuard();
  const createOrder = useMutation({
    mutationFn: async (action: 'draft' | 'confirm') => {
      // A custom IMPORTED mattress mixed with other items is blocked server-side
      // (the import delays everything). Offer to keep them together — the customer
      // agrees to wait — or rethrow so the user can split the order.
      let draft: { id: string };
      // Two guards can stop a create, and each is a question for the customer
      // rather than a mistake to fix: re-ask, then retry with the answer.
      let allowImportMix = false;
      let allowReservation = false;
      const post = (): Promise<{ id: string }> =>
        apiPost<{ id: string }>('/orders', buildOrderBody(allowImportMix, allowReservation));
      for (;;) {
        try {
          draft = await post();
          break;
        } catch (e) {
          const code = getApiError(e).code;
          if (code === 'import_mix_blocked' && !allowImportMix) {
            if (
              !window.confirm(
                'This order has a custom imported mattress that must be specially imported and will take time to arrive.\n\nOK = keep everything on one order and deliver it together once the import lands.\nCancel = go back and put the other items on a separate order.',
              )
            ) {
              throw e;
            }
            allowImportMix = true;
            continue;
          }
          // An imported mattress / sofa bed with none at HQ. It CAN still be
          // sold — as a reservation against the next arrival — but the shop's
          // own floor unit is never used for a delivery, so the customer waits.
          // She has to know that before promising a date.
          if (code === 'reservation_required' && !allowReservation) {
            if (!window.confirm(`${getApiError(e).message}\n\nOK = take the order and reserve it.\nCancel = go back and change the order.`)) {
              throw e;
            }
            allowReservation = true;
            continue;
          }
          throw e;
        }
      }
      if (action === 'confirm') {
        // Recording the mandatory deposit/full payment auto-confirms the draft
        // AND issues the one VAT invoice, in a single atomic batch. This must run
        // FIRST: a bare /confirm on an unpaid draft is rejected (deposit_required)
        // — the deposit is what confirms the order.
        if (paymentsValid(payments, totals.total)) {
          await apiPost(`/payments/orders/${draft.id}/batch`, {
            payments: payments.map((p) => ({
              amount: Number(p.amount),
              method: p.method,
              reference: p.reference.trim() || null,
            })),
          });
        } else {
          // No payment captured (shouldn't happen via this button) — fall back to
          // the explicit confirm, which the backend only allows with an override.
          await apiPost(`/orders/${draft.id}/confirm`);
        }
        // Exchange: apply the return's credit note to the freshly-issued invoice
        // so the customer only settles the difference. Runs AFTER payment so the
        // invoice exists. Non-fatal if it can't apply.
        if (exchange && perms.has('credit_notes.apply')) {
          try {
            const full = await apiGet<{ invoice: { id: string } | null; total: string }>(
              `/orders/${draft.id}`,
            );
            const amount = Math.min(Number(exchange.creditAmount), Number(full.total));
            if (full.invoice && amount > 0) {
              await apiPost(`/credit-notes/${exchange.creditNoteId}/apply`, {
                invoiceId: full.invoice.id,
                amount,
              });
            }
          } catch {
            /* credit can be applied manually from the credit note if this fails */
          }
        }
      }
      return draft.id;
    },
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: ['orders'] });
      cart.clear();
      navigate(`/orders/${id}`);
    },
    onSettled: guard.release,
  });

  // Over-cap discount + no self-approve rights: save the draft and raise an
  // owner approval request instead of confirming. The owner approves from their
  // inbox, then the salesperson confirms + takes payment on the order page.
  const requestApproval = useMutation({
    mutationFn: async () => {
      const body = buildOrderBody();
      const draft = await apiPost<{ id: string }>('/orders', body);
      await apiPost('/discount-approvals', {
        orderId: draft.id,
        reason: approvalReason.trim() || null,
      });
      return draft.id;
    },
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['discount-approvals'] });
      cart.clear();
      navigate(`/orders/${id}`);
    },
    onSettled: guard.release,
  });

  // ── Pickup eligibility + earliest delivery date (category lead times) ─────
  const pickupAllowed = lines.length === 0 || !pickupBlocked;
  // Slowest line gates the order. A SUPPLIER line we hold in matching stock ships
  // next-day (lead 1) like an imported item — mirrors the backend. Factory keeps
  // its production lead (it reserves production-capacity days).
  const earliestLeadDays = lines.reduce((m, l) => {
    const base = LEAD_BY_STRATEGY[l.strategy] ?? 1;
    const fromStock =
      l.strategy === 'MADE_TO_ORDER_SUPPLIER' && lineShipsFromStock(l, fulfillmentType, pickupShopId || null);
    const lead = base > 1 && fromStock ? 1 : base;
    return Math.max(m, lead);
  }, 1);

  // Made-to-order-factory lines also consume daily production capacity, so a date
  // that clears the static lead can still be impossible if the factory is busy.
  // Ask the server for the soonest date with enough headroom for THIS order's
  // quantities, so the picker greys out the days a confirm would reject.
  const factoryLines = useMemo(
    () =>
      lines
        .filter((l) => l.strategy === 'MADE_TO_ORDER_FACTORY' && l.qty > 0)
        .map((l) => ({ productId: l.productId, qty: l.qty })),
    [lines],
  );
  const earliestQuery = useQuery({
    queryKey: ['earliest-delivery', factoryLines],
    queryFn: () =>
      apiPost<{ earliest: string | null }>('/scheduling/earliest-delivery', {
        lines: factoryLines,
      }),
    enabled: factoryLines.length > 0,
  });

  // Earliest delivery = the later of the goods' static lead and the live factory
  // headroom; floors at tomorrow so a delivery is never scheduled for today.
  // Fast Track makes delivery selectable from the next day (the customer paid for
  // priority): the static goods' lead AND the factory-capacity floor are bypassed,
  // and the backend forces those units onto the soonest production day. Without
  // Fast Track the floor is the later of the goods' static lead and live factory
  // headroom, so impossible days stay greyed.
  const earliestDeliveryIso = useMemo(() => {
    if (fastTrack && fulfillmentType === 'delivery') {
      return addDaysIso(businessTodayIso(), 1);
    }
    const staticIso = addDaysIso(businessTodayIso(), Math.max(1, earliestLeadDays));
    const capacityIso = earliestQuery.data?.earliest ?? null;
    return capacityIso && capacityIso > staticIso ? capacityIso : staticIso;
  }, [earliestLeadDays, earliestQuery.data, fastTrack, fulfillmentType]);

  // If the chosen delivery date is now earlier than the lead allows (e.g. a
  // slower item was added after picking), drop it so it must be re-chosen.
  useEffect(() => {
    if (deliveryDate && deliveryDate < earliestDeliveryIso) setDeliveryDate('');
  }, [earliestDeliveryIso, deliveryDate]);

  // ── Step wizard state + per-step validity ────────────────────────────────
  // An out-of-stock line must be reserved (backorder) to proceed.
  const itemsValid =
    lines.length > 0 && !!sellingShopId && !lines.some((l) => lineOutOfStock(l) && !l.backorder);
  const customerValid = !!customer;
  // A backorder order has no known delivery date yet (the stock isn't in). The
  // backend auto-schedules it when the container is received (deliveryDate null →
  // first available slot), so we don't force a date here.
  const hasBackorder = lines.some((l) => l.backorder);
  const fulfilmentValid =
    fulfillmentType === 'pickup'
      ? pickupAllowed && !!(pickupShopId || sellingShopId)
      : !!deliveryAddress.trim() &&
      (hasBackorder || (!!deliveryDate && deliveryDate >= earliestDeliveryIso));
  const stepValid = (s: number): boolean =>
    s === 1 ? itemsValid : s === 2 ? customerValid : s === 3 ? fulfilmentValid : true;
  const canGoTo = (target: number): boolean => {
    // An over-cap order (salesperson) can't advance past the customer step — it
    // must go to the owner first. Lower the discount at step 1 to proceed.
    if (needsApproval && target > 2) return false;
    for (let s = 1; s < target; s++) if (!stepValid(s)) return false;
    return true;
  };

  const pickupShop = shops.find((s) => s.id === (pickupShopId || sellingShopId));
  const custDisplay = customer
    ? customer.type === 'company'
      ? (customer.companyName ?? customer.name)
      : customer.name
    : '';

  return (
    <PageScaffold
      title={['Add items', 'Choose customer', 'Fulfilment', 'Review & confirm'][step - 1]}
      contentClassName="space-y-4"
      subtitle={
        <>
          New order · step {step} of 4 ·{' '}
          {sellingShop ? `Selling at ${sellingShop.name}` : 'Pick a shop'} · {lines.length} line
          {lines.length === 1 ? '' : 's'} · <Money value={totals.total} className="text-ink" />
        </>
      }
      actions={
        <Button
          variant="ghost"
          onClick={() => {
            cart.clear();
            navigate(-1);
          }}
        >
          Discard
        </Button>
      }
      footer={
        <div className="flex items-center justify-between w-full gap-3">
          <div>
            {step > 1 && (
              <Button variant="secondary" onClick={() => setStep(step - 1)}>
                ↝ Back
              </Button>
            )}
          </div>
          <div className="flex items-center gap-4">
            <div className="text-sm text-ink-3 hidden sm:block">
              Total <Money value={totals.total} className="text-ink font-medium ml-1" />
            </div>
            {needsApproval && step >= 2 ? (
              // Over the cap + can't self-approve: intercept as soon as a customer
              // is chosen (step 2) — fulfilment/payment are deferred until the
              // owner approves and the salesperson reopens the order.
              <>
                <Button
                  variant="secondary"
                  disabled={!customerValid}
                  loading={createOrder.isPending}
                  onClick={() => guard.run(() => createOrder.mutate('draft'))}
                >
                  Save draft
                </Button>
                <Button
                  disabled={!customerValid}
                  loading={requestApproval.isPending}
                  title="Send this over-cap discount to the owner for approval"
                  onClick={() => guard.run(() => requestApproval.mutate())}
                >
                  Request owner approval →
                </Button>
              </>
            ) : step < 4 ? (
              <Button
                disabled={!stepValid(step)}
                onClick={() => stepValid(step) && setStep(step + 1)}
              >
                {step === 1 ? 'Customer →' : step === 2 ? 'Fulfilment →' : 'Review →'}
              </Button>
            ) : (
              <>
                <Button
                  variant="secondary"
                  loading={createOrder.isPending}
                  onClick={() => guard.run(() => createOrder.mutate('draft'))}
                >
                  Save draft
                </Button>
                <Button
                  loading={createOrder.isPending}
                  disabled={!canConfirm}
                  title={
                    !paymentsValid(payments, totals.total)
                      ? 'A payment (deposit or full) is required'
                      : !depositMet
                        ? `Deposit must be at least ${Math.round(depositPct * 100)}% (Rs ${minDeposit.toLocaleString()})`
                        : undefined
                  }
                  onClick={() => guard.run(() => createOrder.mutate('confirm'))}
                >
                  Confirm + issue invoice →
                </Button>
              </>
            )}
          </div>
        </div>
      }
    >
      {/* Exchange banner — return credit will be applied on confirm */}
      {exchange && (
        <div className="rounded-md border border-accent/30 bg-accent-soft px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm text-ink">
            <span className="font-medium">Exchange for {exchange.fromNumber}.</span> Return credit{' '}
            <span className="kc-mono">{exchange.creditNumber}</span> of{' '}
            <Money value={exchange.creditAmount} className="font-medium" /> will be applied to this
            order when you confirm.
          </div>
          <span className="text-[11px] uppercase tracking-wider text-accent">Credit pending</span>
        </div>
      )}

      {/* Stepper — click a completed step to jump back */}
      <Stepper
        current={step}
        onStep={(t) => {
          if (canGoTo(t)) setStep(t);
        }}
        steps={[
          { label: 'Items', done: itemsValid },
          { label: 'Customer', done: customerValid },
          { label: 'Fulfilment', done: fulfilmentValid },
          { label: 'Confirm', done: false },
        ]}
      />

      {/* Over-cap discount intercept — appears once a customer is chosen (step 2).
          The salesperson sends it to the owner here; fulfilment + payment are
          deferred to the order page after approval. Owners (canSelfApprove) are
          never blocked, so this never shows for them. */}
      {needsApproval && step >= 2 && (
        <div className="space-y-2 bg-warning-soft text-warning px-3 py-3 rounded border border-warning/30">
          <div className="flex items-start gap-2 text-sm">
            <span className="text-base mt-px">⚠</span>
            <div>
              <div className="font-medium">
                Discount {formatPct(totals.discountPct * 100)} exceeds the {formatPct(capPct * 100)}{' '}
                cap
              </div>
              <div className="text-xs opacity-90 mt-0.5">
                You can't confirm this above the cap. Send it to the owner for approval — once
                approved you'll set fulfilment, confirm, and take payment from the order page.
              </div>
            </div>
          </div>
          <textarea
            value={approvalReason}
            onChange={(e) => setApprovalReason(e.target.value)}
            rows={2}
            placeholder="Reason for the owner (optional) — e.g. loyal customer, price-matched a competitor…"
            className="kc-input w-full text-ink text-sm"
          />
        </div>
      )}

      {/* ── STEP 1 — Items ─────────────────────────────────────────────── */}
      {step === 1 && (
        <div className="space-y-6">
          {isHQUser && (
            <Card
              title="Selling from"
              subtitle="Defaults to the head-office counter — switch to Online or another showroom to register the sale elsewhere."
            >
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">
                  Sale origin
                </span>
                <select
                  value={sellingShopId}
                  onChange={(e) => {
                    setSellingShopId(e.target.value);
                    setSalespersonId('');
                    if (!pickupShopId || pickupShopId === sellingShopId)
                      setPickupShopId(e.target.value);
                  }}
                  className="kc-input"
                >
                  {shops.some((s) => s.type === 'ONLINE') && (
                    <optgroup label="Online">
                      {shops
                        .filter((s) => s.type === 'ONLINE')
                        .map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                    </optgroup>
                  )}
                  {shops.some((s) => s.type === 'SHOP') && (
                    <optgroup label="Showrooms">
                      {shops
                        .filter((s) => s.type === 'SHOP')
                        .map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.code} · {s.name}
                            {s.isHeadOffice ? ' · Head office' : ''}
                          </option>
                        ))}
                    </optgroup>
                  )}
                </select>
              </label>
            </Card>
          )}
          {showSalesperson && (
            <Card
              title="Salesperson"
              subtitle={
                isHeadOfficeShop
                  ? "Optional — leave as Myself to book it under you, or pick a sales girl. Either way it earns commission."
                  : "Attribute this sale to the shop's sales girl — it drives her commission."
              }
            >
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">Sold by</span>
                <select
                  value={salespersonId}
                  onChange={(e) => setSalespersonId(e.target.value)}
                  className="kc-input"
                >
                  <option value="">{isHeadOfficeShop ? 'Myself (head office)' : 'Select a salesperson…'}</option>
                  {salespeople.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                {salespeopleQuery.isLoading && <span className="text-xs text-ink-3">Loading…</span>}
                {!salespeopleQuery.isLoading && !isHeadOfficeShop && salespeople.length === 0 && (
                  <span className="text-xs text-amber-600">
                    No sales girls are assigned to this shop yet — assign one before selling here.
                  </span>
                )}
              </label>
            </Card>
          )}
          <ProductPicker onAdd={addLine} onCustom={setCustomFor} />
          <LinesTable
            lines={lines}
            lineDiscounts={lineDiscounts}
            onUpdate={updateLine}
            onRemove={removeLine}
            vatExempt={vatExempt}
            fulfillmentType={fulfillmentType}
            sellingShopId={sellingShopId || null}
            sellingShopName={sellingShop?.name ?? 'this shop'}
          />
          {/* Order-level discount — one figure for the whole order, spread across
              the items below. Hidden until there's something discountable (every
              line custom-size → nothing to discount). */}
          {discountableGross > 0 && (
            <Card title="Discount" subtitle="Applied across all items">
              <div className="flex items-center justify-between gap-4">
                <span className="text-sm text-ink-3 inline-flex items-center gap-1">
                  On <Money value={discountableGross} muted /> of discountable items
                </span>
                <div className="flex items-center gap-4">
                  <DiscountInput
                    qty={1}
                    unitPrice={discountableGross}
                    mode={orderDiscountMode}
                    value={orderDiscountValue}
                    onChange={(next) => {
                      setOrderDiscountMode(next.mode);
                      setOrderDiscountValue(next.value);
                    }}
                  />
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-ink-3">Discount</div>
                    <Money value={-orderDiscountTotal} className="text-ink" />
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>
      )}

      {customFor?.modelId && (
        <CustomSizeModal
          modelId={customFor.modelId}
          fallbackName={customFor.name}
          onClose={() => setCustomFor(null)}
          onConfirm={(r) => {
            addCustomLine({
              productId: `custom-${crypto.randomUUID()}`,
              product: {
                sku: `Priced as ${r.matchedSize}`,
                name: `${r.modelName} — Custom ${r.widthCm}×${r.lengthCm} cm`,
                vatRate: r.vatRate,
              },
              qty: 1,
              unitPrice: r.unitPrice,
              colorOptions: r.colorOptions,
              sideColorOptions: r.sideColorOptions,
              fabricOptions: r.fabricOptions,
              densityOptions: r.densityOptions,
              selectedColor: r.selectedColor,
              selectedSideColor: r.selectedSideColor,
              selectedFabric: r.selectedFabric,
              selectedDensity: r.selectedDensity,
              deliveryRequired: r.deliveryRequired,
              strategy: 'MADE_TO_ORDER_FACTORY',
              stockLevels: [],
              backorder: false,
              handoverNow: false,
              isCustom: true,
              matchedVariantId: r.matchedVariantId,
              customWidthCm: r.widthCm,
              customLengthCm: r.lengthCm,
              matchedSize: r.matchedSize,
            });
            setCustomFor(null);
          }}
        />
      )}

      {/* ── STEP 2 — Customer ──────────────────────────────────────────── */}
      {step === 2 && (
        <CustomerPicker
          value={customer}
          onChange={setCustomer}
          // Picking a customer jumps straight to fulfilment — unless an over-cap
          // discount must be intercepted here (footer shows the approval CTA).
          onPicked={() => {
            if (!needsApproval) setStep(3);
          }}
        />
      )}

      {/* ── STEP 3 — Fulfilment ────────────────────────────────────────── */}
      {step === 3 && (
        <div className="max-w-2xl">
          <FulfilmentChooser
            fulfillmentType={fulfillmentType}
            setFulfillmentType={setFulfillmentType}
            pickupAllowed={pickupAllowed}
            stockShortNames={stockShortLines.map((b) => b.line.product.name)}
            hasBackorder={hasBackorder}
            earliestDate={earliestDeliveryIso}
            pickupShopId={pickupShopId}
            setPickupShopId={setPickupShopId}
            deliveryAddress={deliveryAddress}
            setDeliveryAddress={setDeliveryAddress}
            deliveryDate={deliveryDate}
            setDeliveryDate={setDeliveryDate}
            disposalQty={disposalQty}
            setDisposalQty={setDisposalQty}
            fastTrack={fastTrack}
            setFastTrack={setFastTrack}
            sellingShopId={sellingShopId || null}
            region={orderRegion}
            setRegion={(r) => {
              setOrderRegion(r);
              if (customer && !customer.region) {
                apiPatch(`/customers/${customer.id}`, { region: r }).catch(() => undefined);
                setCustomer({ ...customer, region: r });
              }
            }}
            customer={customer}
          />
        </div>
      )}

      {/* ── STEP 4 — Review & confirm ──────────────────────────────────── */}
      {step === 4 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card
              title="Review"
              subtitle="Check everything before placing the order"
              bodyClassName="divide-y divide-rule-2"
            >
              {/* Customer */}
              <div className="flex items-start justify-between gap-3 pb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-ink-3">Customer</div>
                  <div className="text-ink font-medium">{custDisplay}</div>
                  <div className="text-xs text-ink-3 kc-mono">
                    {customer?.customerCode} · {customer?.phone}
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setStep(2)}>
                  Edit
                </Button>
              </div>
              {/* Fulfilment */}
              <div className="flex items-start justify-between gap-3 py-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-ink-3">Fulfilment</div>
                  {fulfillmentType === 'pickup' ? (
                    <div className="text-ink">
                      Pickup ·{' '}
                      {pickupShop ? `${pickupShop.code} · ${pickupShop.name}` : 'selling shop'}
                    </div>
                  ) : (
                    <div className="text-ink">
                      Delivery · {deliveryAddress || '—'}
                      {deliveryDate ? (
                        <span className="text-ink-3">
                          {' '}
                          ·{' '}
                          {formatBusinessDate(deliveryDate, {
                            weekday: 'short',
                            day: 'numeric',
                            month: 'short',
                          })}
                        </span>
                      ) : hasBackorder ? (
                        <span className="text-ink-3"> · auto — scheduled when stock arrives</span>
                      ) : null}
                    </div>
                  )}
                  {fulfillmentType === 'delivery' && disposalQty > 0 && (
                    <div className="text-xs text-ink-3 mt-0.5">
                      + collect {disposalQty} old mattress{disposalQty === 1 ? '' : 'es'}
                    </div>
                  )}
                </div>
                <Button variant="ghost" size="sm" onClick={() => setStep(3)}>
                  Edit
                </Button>
              </div>
              {/* Items */}
              <div className="pt-3">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-wider text-ink-3">
                    Items ({lines.length})
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setStep(1)}>
                    Edit
                  </Button>
                </div>
                <ul className="mt-2 divide-y divide-rule-2">
                  {lines.map((l) => {
                    const finish = [
                      l.selectedColor,
                      l.selectedSideColor,
                      l.selectedFabric,
                      l.selectedDensity,
                    ]
                      .filter(Boolean)
                      .join(' / ');
                    return (
                      <li
                        key={l.productId}
                        className="flex items-baseline justify-between gap-3 py-1.5 text-sm"
                      >
                        <span className="text-ink">
                          {l.qty} × {l.product.name}
                          {finish && <span className="text-ink-3"> · {finish}</span>}
                          {l.handoverNow && (
                            <span className="text-accent"> · handed over now at the shop</span>
                          )}
                        </span>
                        <Money
                          value={l.qty * l.unitPrice - (lineDiscounts.get(l.productId) ?? 0)}
                          className="text-ink-3"
                        />
                      </li>
                    );
                  })}
                </ul>
              </div>
            </Card>

            {/* Owner / admin aren't capped — just a heads-up that this is over the
                staff cap; they can apply it directly. (Salespeople are intercepted
                back at the customer step with a request-approval action.) */}
            {overCap && canSelfApprove && (
              <div className="flex items-start gap-2 text-sm bg-info-soft text-info px-3 py-2.5 rounded border border-info/30">
                <span className="text-base mt-px">ℹ</span>
                <div>
                  <div className="font-medium">
                    Discount {formatPct(totals.discountPct * 100)} is above the{' '}
                    {formatPct(capPct * 100)} staff cap
                  </div>
                  <div className="text-xs opacity-90 mt-0.5">
                    You can apply it directly — owner / admin aren't capped.
                  </div>
                </div>
              </div>
            )}

            {(createOrder.isError || requestApproval.isError) && (
              <div className="text-sm bg-negative-soft text-negative px-3 py-2 rounded border border-negative/20">
                {(() => {
                  // getApiError surfaces the backend's actionable message (an
                  // AxiosError IS an Error, so checking instanceof first would
                  // always show the useless "Request failed with status code N").
                  const err = createOrder.error ?? requestApproval.error;
                  return getApiError(err).message;
                })()}
              </div>
            )}
          </div>

          <div className="space-y-4">
            <Card title="Summary">
              <dl className="space-y-2 text-sm">
                <Row label="Subtotal" value={<Money value={totals.subtotal} />} />
                {totals.discountTotal > 0 && (
                  <Row label="Discounts" value={<Money value={-totals.discountTotal} />} />
                )}
                <Row
                  label={vatExempt ? 'VAT (exempt)' : 'VAT (15%)'}
                  value={
                    vatExempt ? (
                      <span className="text-ink-3 italic">—</span>
                    ) : (
                      <Money value={totals.vat} />
                    )
                  }
                />
                {vatExempt && (
                  <div className="text-xs text-info bg-info-soft rounded px-2 py-1 mt-1 border border-info/20">
                    Customer is VAT-excluded — invoice will not charge VAT.
                  </div>
                )}
                {totals.fastTrackFee > 0 && (
                  <Row
                    label="Fast Track Delivery"
                    value={<Money value={totals.fastTrackFee} />}
                  />
                )}
                <div className="border-t border-rule pt-2 mt-2 flex justify-between text-base font-medium">
                  <span className="text-ink">Total</span>
                  <Money value={totals.total} className="text-accent text-lg kc-serif" />
                </div>
              </dl>
              <Input
                label="Internal note"
                placeholder="Anything operations or finance should see…"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="mt-4"
              />
            </Card>

            {/* Payment is required on every order — full settlement or a deposit. */}
            <Card
              title="Payment"
              subtitle={`Required — full amount, or at least a ${Math.round(depositPct * 100)}% deposit`}
            >
              <PaymentCapture
                lines={payments}
                onChange={setPayments}
                total={totals.total}
                userName={user?.name}
                shopName={sellingShop?.name}
                canRecordCheque={perms.has('payments.record_cheque')}
                depositOption={{
                  label: `Deposit ${Math.round(depositPct * 100)}%`,
                  // Round UP so the one-tap deposit can never land a fraction of
                  // a cent below the server's exact threshold (which would block
                  // fulfilment). At most ~1 cent over the minimum.
                  value: Math.ceil(totals.total * depositPct * 100) / 100,
                }}
              />
              {paymentsTotal(payments) > totals.total + 0.001 && (
                <div className="mt-2 text-xs text-negative">
                  Payment can't exceed the order total ({totals.total.toLocaleString()}).
                </div>
              )}
              {!paymentsValid(payments, totals.total) ? (
                <div className="mt-2 text-xs text-ink-3">
                  Enter a payment (deposit or full) to confirm the order.
                </div>
              ) : !depositMet ? (
                <div className="mt-2 text-xs text-negative">
                  Deposit must be at least {Math.round(depositPct * 100)}% — Rs{' '}
                  {minDeposit.toLocaleString()}.
                </div>
              ) : null}
            </Card>
          </div>
        </div>
      )}
    </PageScaffold>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Product picker
// ─────────────────────────────────────────────────────────────────────────────
export function ProductPicker({ onAdd, onCustom }: { onAdd: (p: Product) => void; onCustom: (p: Product) => void }): JSX.Element {
  const [q, setQ] = useState('');
  const query = q.trim();
  const search = useQuery({
    queryKey: ['product-search', query],
    queryFn: () => apiGet<Product[]>('/products', { q: query }),
    enabled: query.length > 0,
  });
  const list = (search.data ?? []).slice(0, 12);
  return (
    <Card title="Add products" subtitle="Search by SKU or name and tap to add">
      <Input
        id="prod-q"
        placeholder="Search products…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        autoComplete="off"
      />

      {query.length === 0 ? (
        <p className="mt-3 text-xs text-ink-3">
          Start typing a product name or SKU, or browse the{' '}
          <Link to="/catalogue" className="text-accent hover:underline">
            catalogue
          </Link>{' '}
          and tap any product.
        </p>
      ) : (
        <div className="mt-3 border border-rule rounded-md divide-y divide-rule-2 max-h-72 overflow-y-auto">
          {search.isLoading ? (
            <div className="text-sm text-ink-3 px-4 py-6 text-center">Searching…</div>
          ) : list.length === 0 ? (
            <div className="text-sm text-ink-3 px-4 py-6 text-center">
              No products match “{query}”.
            </div>
          ) : (
            list.map((p) => {
              const onSale = !!p.salePrice;
              const stocked = p.category.fulfillmentStrategy === 'STOCK_AT_LOCATION';
              const total = (p.stockLevels ?? []).reduce(
                (s, lv) => s + Math.max(0, lv.onHand - lv.reserved - lv.display),
                0,
              );
              const outOfStock = stocked && total === 0;
              // Locally made-to-order mattresses can be built to a bespoke size.
              const canCustomSize = !!p.modelId && p.category.allowsCustomSize;
              return (
                <div key={p.id} className="flex items-stretch transition hover:bg-paper-2">
                  <button
                    type="button"
                    onClick={() => onAdd(p)}
                    className="flex-1 min-w-0 text-left px-4 py-2.5 flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <div className="text-sm text-ink truncate flex items-center gap-2">
                        {p.name}
                        {outOfStock && (
                          <span className="text-[9px] uppercase tracking-wider bg-warning-soft text-warning px-1.5 py-0.5 rounded">
                            Out of stock · reserve
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-ink-3 kc-mono truncate">{p.sku} · {p.category.name}</div>
                    </div>
                    <div className="shrink-0 text-right leading-tight">
                      <Money value={p.salePrice ?? p.basePrice} className={cn('text-sm', onSale && 'text-accent font-medium')} />
                      {onSale && <Money value={p.basePrice} className="block text-[10px] text-ink-4 line-through" />}
                    </div>
                  </button >
                  {canCustomSize && (
                    <button
                      type="button"
                      onClick={() => onCustom(p)}
                      className="shrink-0 px-3 my-1.5 mr-2 ml-1 border-l border-rule-2 text-[11px] font-medium text-accent hover:underline"
                      title="Order a bespoke width × length"
                    >
                      Custom size
                    </button>
                  )
                  }
                </div >
              );
            })
          )}
        </div >
      )}
    </Card >
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Lines table
// ─────────────────────────────────────────────────────────────────────────────
function LinesTable({
  lines,
  lineDiscounts,
  onUpdate,
  onRemove,
  vatExempt,
  fulfillmentType,
  sellingShopId,
  sellingShopName,
}: {
  lines: LineDraft[];
  lineDiscounts: Map<string, number>;
  onUpdate: (id: string, patch: Partial<LineDraft>) => void;
  onRemove: (id: string) => void;
  vatExempt: boolean;
  fulfillmentType: FulfillmentType;
  sellingShopId: string | null;
  sellingShopName: string;
}): JSX.Element {
  if (lines.length === 0) {
    return (
      <Card title="Line items" bodyClassName="p-0">
        <EmptyState
          title="No items yet"
          description="Use the search above, or browse the catalogue and tap any product."
        />
      </Card>
    );
  }
  return (
    <Card
      title={`Line items (${lines.length})`}
      subtitle={`${lines.reduce((s, l) => s + l.qty, 0)} units`}
      bodyClassName="p-0"
    >
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
            <th className="px-6 py-2 font-medium">Product</th>
            <th className="px-3 py-2 font-medium text-right">Qty</th>
            <th className="px-3 py-2 font-medium text-right">Unit</th>
            <th className="px-3 py-2 font-medium text-right">VAT</th>
            <th className="px-6 py-2 font-medium text-right">Total</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => {
            // Each line carries its allocated share of the order-level discount.
            const lineDisc = lineDiscounts.get(l.productId) ?? 0;
            // VAT-inclusive catalogue price → extract VAT, don't add on top.
            const grossIncl = l.qty * l.unitPrice - lineDisc;
            const r = l.product.vatRate;
            const lineVat = vatExempt ? 0 : Math.round(((grossIncl * r) / (1 + r)) * 100) / 100;
            const lineNet = grossIncl - lineVat;
            return (
              <tr key={l.productId} className="border-b border-rule-2">
                <td className="px-6 py-3">
                  <div className="text-ink flex items-center gap-2">
                    {l.product.name}
                    {l.isCustom && (
                      <span className="text-[9px] uppercase tracking-wider bg-accent-soft text-accent px-1.5 py-0.5 rounded">
                        Custom
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-ink-3 kc-mono">{l.product.sku}</div>
                  {l.colorOptions?.length ||
                    l.sideColorOptions?.length ||
                    l.fabricOptions?.length ||
                    l.densityOptions?.length ? (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {l.colorOptions?.length ? (
                        <select
                          value={l.selectedColor ?? ''}
                          onChange={(e) =>
                            onUpdate(l.productId, { selectedColor: e.target.value || null })
                          }
                          className="kc-input kc-input-sm w-auto"
                        >
                          <option value="">colour…</option>
                          {l.colorOptions.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                      ) : null}
                      {l.sideColorOptions?.length ? (
                        <select
                          value={l.selectedSideColor ?? ''}
                          onChange={(e) =>
                            onUpdate(l.productId, { selectedSideColor: e.target.value || null })
                          }
                          className="kc-input kc-input-sm w-auto"
                        >
                          <option value="">side…</option>
                          {l.sideColorOptions.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                      ) : null}
                      {l.fabricOptions?.length ? (
                        <select
                          value={l.selectedFabric ?? ''}
                          onChange={(e) =>
                            onUpdate(l.productId, { selectedFabric: e.target.value || null })
                          }
                          className="kc-input kc-input-sm w-auto"
                        >
                          <option value="">fabric…</option>
                          {l.fabricOptions.map((f) => (
                            <option key={f} value={f}>
                              {f}
                            </option>
                          ))}
                        </select>
                      ) : null}
                      {l.densityOptions?.length ? (
                        <select
                          value={l.selectedDensity ?? ''}
                          onChange={(e) =>
                            onUpdate(l.productId, { selectedDensity: e.target.value || null })
                          }
                          className="kc-input kc-input-sm w-auto"
                        >
                          <option value="">density…</option>
                          {l.densityOptions.map((d) => (
                            <option key={d} value={d}>
                              {d}
                            </option>
                          ))}
                        </select>
                      ) : null}
                    </div>
                  ) : null}
                  {l.strategy === 'STOCK_AT_LOCATION' &&
                  !l.deliveryRequired &&
                  !l.isCustom &&
                  fulfillmentType === 'delivery' &&
                  availableAtShop(l, sellingShopId) >= l.qty ? (
                    // Hand-over-now: the shop shelf can cover this line — the
                    // customer can take it at the counter when the order
                    // confirms (that draws THIS shop's stock), or it ships from
                    // the warehouse with the rest of the delivery.
                    <label className="flex items-center gap-1.5 mt-2 text-[11px] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={l.handoverNow}
                        onChange={(e) =>
                          onUpdate(l.productId, { handoverNow: e.target.checked, handoverNowTouched: true })
                        }
                        className="accent-accent"
                      />
                      <span className={l.handoverNow ? 'text-accent' : 'text-ink-3'}>
                        {l.handoverNow
                          ? `Hand over now — customer takes it from ${sellingShopName} at payment`
                          : `In stock here — tick to hand over now, or it ships from the warehouse`}
                      </span>
                    </label>
                  ) : null}
                  {l.strategy === 'STOCK_AT_LOCATION' && !l.deliveryRequired && l.backorder ? (
                    // Accessory backorder — derived from fulfilment + availability.
                    // Pickup = collect-from-HQ; delivery = reserve against incoming.
                    <div className="mt-2 text-[11px] text-accent flex items-start gap-1">
                      <span>📦</span>
                      <span>
                        {fulfillmentType === 'pickup'
                          ? 'Out at this shop — collect from the warehouse. We transfer it in; the customer picks it up here.'
                          : 'No warehouse stock for the delivery — reserved against incoming stock (delivers when it arrives).'}
                      </span>
                    </div>
                  ) : (lineOutOfStock(l) || l.backorder) ? (
                    // Imported (delivery-required) out-of-stock line — opt in to backorder.
                    <label className="flex items-center gap-1.5 mt-2 text-[11px] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={l.backorder}
                        onChange={(e) => onUpdate(l.productId, { backorder: e.target.checked })}
                        className="accent-accent"
                      />
                      <span className={l.backorder ? 'text-accent' : 'text-negative'}>
                        {l.backorder
                          ? 'Reserved — backorder against incoming stock (delivers when it arrives)'
                          : 'Out of stock — tick to reserve against incoming'}
                      </span>
                    </label>
                  ) : null}
                  {/* Free-gift opt-in — the sales girl chooses whether to bundle the
                      model's gift with this line (no longer auto-added). */}
                  {(l.gifts?.length ?? 0) > 0 && (
                    <>
                      <label className="flex items-start gap-1.5 mt-2 text-[11px] cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!l.includeGift}
                          onChange={(e) => onUpdate(l.productId, { includeGift: e.target.checked })}
                          className="accent-accent mt-0.5"
                        />
                        <span className={l.includeGift ? 'text-accent' : 'text-ink-3'}>
                          🎁 Add free gift
                          {l.gifts!.length === 1 ? ` (${l.gifts![0]?.name})` : ` (${l.gifts!.length} items)`}
                        </span>
                      </label>
                      {/* Size picker for each MODEL gift — the sales girl chooses
                          which size the customer gets. Only shown once opted in. */}
                      {l.includeGift &&
                        l.gifts!
                          .filter((g) => g.giftModelId && (g.sizeOptions?.length ?? 0) > 0)
                          .map((g) => (
                            <div
                              key={g.giftModelId}
                              className="flex items-center gap-1.5 mt-1 ml-5 text-[11px]"
                            >
                              <span className="text-ink-3">{g.name} size</span>
                              <select
                                className="kc-input kc-input-sm py-0.5"
                                value={l.giftSelections?.[g.giftModelId!] ?? ''}
                                onChange={(e) =>
                                  onUpdate(l.productId, {
                                    giftSelections: {
                                      ...l.giftSelections,
                                      [g.giftModelId!]: e.target.value,
                                    },
                                  })
                                }
                              >
                                {g.sizeOptions!.map((o) => {
                                  const short = o.available != null && o.available < g.qty;
                                  return (
                                    <option key={o.productId} value={o.productId}>
                                      {o.size ?? o.sku}
                                      {short ? ' — out of stock' : ''}
                                    </option>
                                  );
                                })}
                              </select>
                            </div>
                          ))}
                    </>
                  )}
                </td>
                <td className="px-3 py-3 text-right">
                  <input
                    type="number"
                    min={1}
                    value={l.qty}
                    onChange={(e) =>
                      onUpdate(l.productId, { qty: Math.max(1, Number(e.target.value)) })
                    }
                    className="kc-input w-16 text-right kc-mono"
                  />
                </td>
                <td className="px-3 py-3 text-right">
                  {
                    l.isCustom ? (
                      // Bespoke price is server-derived from the size ladder — locked
                      // here, and custom sizes are never discountable (see below).
                      <span className="inline-flex items-center justify-end gap-1 kc-mono text-ink" title="Priced from the standard size ladder">
                        <Money value={l.unitPrice} />
                        <span className="text-ink-4">🔒</span>
                      </span>
                    ) : (
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        value={l.unitPrice}
                        onChange={(e) => onUpdate(l.productId, { unitPrice: Math.max(0, Number(e.target.value)) })}
                        className="kc-input w-24 text-right kc-mono"
                      />
                    )
                  }
                </td >
                <td className="px-3 py-3 text-right kc-mono text-ink-3">
                  {vatExempt ? <span className="italic">—</span> : <Money value={lineVat} muted />}
                </td>
                <td className="px-6 py-3 text-right">
                  <Money value={lineNet + lineVat} />
                </td>
                <td className="pr-4">
                  <IconButton
                    aria-label={`Remove ${l.product.name}`}
                    variant="danger"
                    size="sm"
                    onClick={() => onRemove(l.productId)}
                  >
                    ×
                  </IconButton>
                </td>
              </tr >
            );
          })}
          {/* Free gifts — derived from the lines that OPTED IN (includeGift), so a
              gift only appears once the sales girl ticks its line. Display-only;
              the backend adds them for real (and skips any that are out of stock). */}
          {(() => {
            const agg = new Map<string, { name: string; sku: string; qty: number; available: number | null }>();
            for (const l of lines) {
              if (!l.includeGift) continue;
              for (const g of l.gifts ?? []) {
                // Model gifts resolve to the sales girl's chosen size; fixed gifts
                // use their own product. Skip a model gift with no size picked yet.
                let key: string;
                let name: string;
                let sku: string;
                let available: number | null;
                if (g.giftModelId) {
                  const chosenId = l.giftSelections?.[g.giftModelId];
                  const opt = g.sizeOptions?.find((o) => o.productId === chosenId);
                  if (!opt) continue;
                  key = opt.productId;
                  name = `${g.name} — ${opt.size ?? opt.sku}`;
                  sku = opt.sku;
                  available = opt.available;
                } else {
                  key = g.productId!;
                  name = g.name;
                  sku = g.sku ?? '';
                  available = g.available;
                }
                const slot = agg.get(key) ?? { name, sku, qty: 0, available };
                slot.qty += g.qty * l.qty;
                agg.set(key, slot);
              }
            }
            return [...agg.entries()].map(([pid, g]) => {
              const short = g.available != null && g.available < g.qty;
              return (
                <tr key={`gift-${pid}`} className="border-b border-rule-2 bg-accent-soft/30">
                  <td className="px-6 py-3">
                    <div className="text-ink flex items-center gap-2">
                      {g.name}
                      <span className="text-[9px] uppercase tracking-wider bg-accent-soft text-accent px-1.5 py-0.5 rounded">
                        🎁 Free gift
                      </span>
                    </div>
                    <div className="text-xs text-ink-3 kc-mono">{g.sku}</div>
                    {short && (
                      <div className="text-[11px] text-warning mt-0.5">
                        Out of stock ({g.available} of {g.qty}) — won’t be added to the order.
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3 text-right kc-mono">{g.qty}</td>
                  <td className="px-3 py-3 text-right text-ink-3">Free</td>
                  <td className="px-3 py-3 text-right text-ink-4">—</td>
                  <td className="px-6 py-3 text-right text-ink-3">Free</td>
                  <td />
                </tr>
              );
            });
          })()}
        </tbody >
      </table >
    </Card >
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Customer picker — supports individual + company
// ─────────────────────────────────────────────────────────────────────────────
export function CustomerPicker({
  value,
  onChange,
  onPicked,
}: {
  value: Customer | null;
  onChange: (c: Customer | null) => void;
  /** Fired when the user actively commits a customer (creates or selects one),
   *  so the wizard can jump straight to fulfilment instead of making them click
   *  "Fulfilment →". Not fired when merely editing an already-selected record. */
  onPicked?: () => void;
}): JSX.Element {
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);

  const search = useQuery({
    queryKey: ['customer-search', q],
    queryFn: () => apiGet<Customer[]>('/customers/lookup', { q }),
    enabled: q.length >= 3,
  });

  if (value) {
    return (
      <CustomerCreateForm
        key={value.id}
        customer={value}
        cancelLabel="Change customer"
        onCreated={onChange}
        onCancel={() => onChange(null)}
      />
    );
  }

  if (creating) {
    return (
      <CustomerCreateForm
        onCreated={(c) => {
          onChange(c);
          onPicked?.();
        }}
        onCancel={() => setCreating(false)}
        initialPhone={/^\+?[\d\s-]+$/.test(q) ? q : ''}
      />
    );
  }

  return (
    <Card
      title="Customer"
      subtitle="Find an existing customer by NIC or phone — or create a new record"
    >
      <div className="space-y-3">
        <Input
          id="cust-q"
          placeholder="Enter NIC or phone (min 3 characters)…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div className="border border-rule rounded divide-y divide-rule">
          {q.length < 3 ? (
            <div className="text-sm text-ink-3 px-4 py-3">
              Type at least 3 characters of a NIC or phone number.
            </div>
          ) : search.isLoading ? (
            <div className="text-sm text-ink-3 px-4 py-3">Searching…</div>
          ) : (search.data ?? []).length === 0 ? (
            <div className="text-sm text-ink-3 px-4 py-3">
              No matches.{' '}
              <Button type="button" variant="tertiary" size="xs" onClick={() => setCreating(true)}>
                Create a new customer
              </Button>
            </div>
          ) : (
            (search.data ?? []).map((c) => (
              <button
                key={c.id}
                className="w-full text-left px-4 py-2.5 hover:bg-paper-2 transition flex items-baseline justify-between gap-3"
                onClick={() => {
                  onChange(c);
                  onPicked?.();
                }}
              >
                <div>
                  <div className="text-sm text-ink flex items-center gap-2">
                    {c.type === 'company' ? (c.companyName ?? c.name) : c.name}
                    {c.type === 'company' && (
                      <span className="text-[10px] uppercase tracking-wider text-accent">
                        company
                      </span>
                    )}
                    {c.vatExcluded && (
                      <span className="text-[10px] uppercase tracking-wider text-info">
                        VAT excl.
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-ink-3 kc-mono">
                    {c.phone}
                    {c.email ? ` · ${c.email}` : ''}
                  </div>
                </div>
                <span className="text-xs text-ink-4 kc-mono">{c.customerCode}</span>
              </button>
            ))
          )}
        </div>
        <Button type="button" variant="tertiary" size="xs" onClick={() => setCreating(true)}>
          + Create a new customer
        </Button>
      </div>
    </Card>
  );
}
// ─────────────────────────────────────────────────────────────────────────────
// Fulfilment chooser — 2 tiles: pickup | delivery
// ─────────────────────────────────────────────────────────────────────────────
function FulfilmentChooser({
  fulfillmentType,
  setFulfillmentType,
  pickupAllowed,
  stockShortNames,
  hasBackorder,
  earliestDate,
  pickupShopId,
  setPickupShopId,
  deliveryAddress,
  setDeliveryAddress,
  deliveryDate,
  setDeliveryDate,
  disposalQty,
  setDisposalQty,
  fastTrack,
  setFastTrack,
  sellingShopId,
  region,
  setRegion,
  customer,
}: {
  fulfillmentType: FulfillmentType;
  setFulfillmentType: (t: FulfillmentType) => void;
  pickupAllowed: boolean;
  stockShortNames: string[];
  hasBackorder: boolean;
  earliestDate: string;
  pickupShopId: string;
  setPickupShopId: (s: string) => void;
  deliveryAddress: string;
  setDeliveryAddress: (s: string) => void;
  deliveryDate: string;
  setDeliveryDate: (s: string) => void;
  disposalQty: number;
  setDisposalQty: (n: number) => void;
  fastTrack: boolean;
  setFastTrack: (b: boolean) => void;
  sellingShopId: string | null;
  region: Region | null;
  setRegion: (r: Region) => void;
  customer: Customer | null;
}): JSX.Element {
  const shops = useQuery({ queryKey: ['shops'], queryFn: () => apiGet<Shop[]>('/shops') });
  const pickupHint = pickupAllowed
    ? 'Customer collects in store'
    : stockShortNames.length > 0
      ? 'Not enough shop stock — fulfilling from HQ requires delivery'
      : 'Only for accessories — this order must be delivered';

  return (
    <Card title="Fulfilment">
      {stockShortNames.length > 0 && (
        <div className="mb-3 flex items-start gap-2 text-sm bg-info-soft text-info border border-info/30 rounded px-3 py-2.5">
          <span className="text-base mt-px">ℹ</span>
          <div>
            <div className="font-medium">Pickup unavailable — shop stock short</div>
            <div className="text-xs opacity-90 mt-0.5">
              {stockShortNames.slice(0, 3).join(', ')}
              {stockShortNames.length > 3 ? ` +${stockShortNames.length - 3} more` : ''} will ship
              from the HQ warehouse, so this order is on delivery only.
            </div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Tile
          icon="🝬"
          label="Pickup at shop"
          hint={pickupHint}
          active={fulfillmentType === 'pickup'}
          disabled={!pickupAllowed}
          onClick={() => pickupAllowed && setFulfillmentType('pickup')}
        />
        <Tile
          icon="🚚"
          label="Delivery"
          hint="Ship to customer address"
          active={fulfillmentType === 'delivery'}
          onClick={() => setFulfillmentType('delivery')}
        />
      </div>

      {fulfillmentType === 'pickup' && (
        <div className="mt-4 space-y-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">
              Pickup shop
            </span>
            <select
              value={pickupShopId || sellingShopId || ''}
              onChange={(e) => setPickupShopId(e.target.value)}
              className="kc-input"
            >
              {(shops.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.code} · {s.name}
                  {s.id === sellingShopId ? ' (selling shop)' : ''}
                </option>
              ))}
            </select>
            <span className="text-xs text-ink-3">
              Defaults to the shop you're selling at. Change if the customer wants to pick up
              elsewhere.
            </span>
          </label>
        </div>
      )}

      {fulfillmentType === 'delivery' && (
        <div className="mt-4 space-y-3">
          <AddressAutocomplete
            label="Delivery address"
            placeholder="Search the map — street, town, or nearest point"
            value={deliveryAddress}
            onChange={setDeliveryAddress}
          />
          {/* Precise address lives on the customer record now — show it read-only
              so ops sees what will print. Editable from the customer step. */}
          {customer?.addressPrecise && (
            <div className="rounded-md border border-dashed border-rule p-3 text-xs">
              <div className="font-medium uppercase tracking-wide text-ink-3">Precise address · from customer</div>
              <div className="whitespace-pre-line text-ink mt-1">{customer.addressPrecise}</div>
              <div className="text-ink-4 mt-1">Prints on the invoice &amp; delivery note. Edit it on the customer record.</div>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-ink-3 uppercase tracking-wide mb-1.5">
              Delivery date{hasBackorder && <span className="text-ink-4"> · optional</span>}
            </label>
            {hasBackorder && (
              <div className="mb-2 flex items-start gap-2 text-sm bg-info-soft text-info border border-info/30 rounded px-3 py-2.5">
                <span className="text-base mt-px">📦</span>
                <div>
                  <div className="font-medium">Delivery auto-scheduled when the stock arrives</div>
                  <div className="text-xs opacity-90 mt-0.5">
                    This order reserves a backordered item, so the date is set automatically once
                    the container is received. Leave it blank — or pick a target date below.
                  </div>
                </div>
              </div>
            )}
            <DeliveryDatePicker
              region={region}
              onRegionChange={setRegion}
              value={deliveryDate}
              onChange={setDeliveryDate}
              earliestDate={earliestDate}
              priority={fastTrack}
              customerNeedsRegion={!customer?.region}
              customerLabel={
                customer?.type === 'company'
                  ? (customer.companyName ?? customer.name)
                  : customer?.name
              }
            />
          </div>
          {/* Old-mattress disposal — free service, but we track qty for ops */}
          <div className="border border-dashed border-rule rounded-md p-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm text-ink">Old mattress collection</div>
                <div className="text-xs text-ink-3 mt-0.5">
                  Free service · we'll dispose of them. How many?
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setDisposalQty(Math.max(0, disposalQty - 1))}
                  className="w-7 h-7 rounded border border-rule text-ink-2 hover:bg-paper-2 hover:text-ink transition"
                  aria-label="Decrease"
                  disabled={disposalQty === 0}
                >
                  −
                </button>
                <input
                  type="number"
                  min="0"
                  max="20"
                  value={disposalQty}
                  onChange={(e) =>
                    setDisposalQty(Math.max(0, Math.min(20, Number(e.target.value || 0))))
                  }
                  className="w-14 text-center kc-input"
                />
                <button
                  type="button"
                  onClick={() => setDisposalQty(Math.min(20, disposalQty + 1))}
                  className="w-7 h-7 rounded border border-rule text-ink-2 hover:bg-paper-2 hover:text-ink transition"
                  aria-label="Increase"
                >
                  +
                </button>
              </div>
            </div>
            {disposalQty > 0 && (
              <div className="mt-2 text-xs text-accent">
                ✓ Driver will collect {disposalQty} old mattress{disposalQty === 1 ? '' : 'es'} on
                delivery.
              </div>
            )}
          </div>
          {/* Fast Track (priority) delivery — flat VAT-inclusive fee (T&C §9) */}
          <label className="border border-dashed border-rule rounded-md p-3 flex items-start justify-between gap-3 cursor-pointer">
            <div>
              <div className="text-sm text-ink">⚡ Fast Track delivery</div>
              <div className="text-xs text-ink-3 mt-0.5">
                Priority delivery — a flat{' '}
                <Money value={FAST_TRACK_DELIVERY_FEE} className="font-medium" /> (incl. VAT) is
                added to the invoice. Delivery can be scheduled from the next day.
              </div>
            </div>
            <input
              type="checkbox"
              checked={fastTrack}
              onChange={(e) => setFastTrack(e.target.checked)}
              className="mt-1 h-4 w-4 shrink-0 accent-accent"
            />
          </label>
        </div>
      )}
    </Card>
  );
}

function Tile({
  icon,
  label,
  hint,
  active,
  disabled,
  onClick,
}: {
  icon: string;
  label: string;
  hint: string;
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'p-4 rounded-lg border-2 transition text-left flex flex-col gap-1',
        disabled
          ? 'bg-paper-2/50 border-rule opacity-50 cursor-not-allowed'
          : active
            ? 'bg-accent-soft border-accent'
            : 'bg-paper-2 border-rule hover:border-accent/40',
      )}
    >
      <span className="text-2xl">{icon}</span>
      <span className={cn('text-sm font-medium', active && !disabled ? 'text-accent' : 'text-ink')}>
        {label}
      </span>
      <span className={cn('text-xs', active && !disabled ? 'text-accent/80' : 'text-ink-3')}>
        {hint}
      </span>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// shared
// ─────────────────────────────────────────────────────────────────────────────
function Row({ label, value }: { label: string; value: React.ReactNode }): JSX.Element {
  return (
    <div className="flex justify-between text-ink-3">
      <span>{label}</span>
      <span className="kc-mono kc-tnum text-ink">{value}</span>
    </div>
  );
}
